export default (() =>
    class extends wealthify.componentBases.WebComponent {

        constructor (config) {
            super(config);

            this.styleSelectors.add(this.constructor.buttonBaseStyleSelector);

            this.onConnectedCallback = () => {
                this.deprecatedAttachAdditionalProps(this.additionalProps);
            };
        }

        // Public

        static buttonBaseStyleSelector = 'c-button-base';

        static baseVariantEnums = Object.freeze({
            Primary: 'primary',
            Secondary: 'secondary',
            Tertiary: 'tertiary',
            Danger: 'danger'
        });

        static linkTargetEnums = Object.freeze({
            Blank: 'blank',
            Parent: 'parent',
            Self: 'self',
            Top: 'top'
        });

        static iconSizeMap = Object.freeze({
            [wealthify.enumsLocal.sizes.Micro]: '75',
            [wealthify.enumsLocal.sizes.Small]: '100',
            [wealthify.enumsLocal.sizes.Medium]: '100'
        });

        static iconNoLabelSize = '150';

        static defaultVariant = this.baseVariantEnums.Primary;

        static defaultSize = wealthify.enumsLocal.sizes.Medium;

        static defaultLinkTarget = this.linkTargetEnums.Self;

        static get validVariants () {
            return [
                ...Object.values(this.baseVariantEnums),
                ...Object.values(this.variantEnums ?? [])
            ];
        }

        static get validSizes () {
            return [
                ...Object.values(wealthify.enumsLocal.sizes),
                ...Object.values(this.sizeEnums ?? [])
            ];
        }

        static get validLinkTargets () {
            return [
                ...Object.values(this.linkTargetEnums)
            ];
        }

        additionalProps = Object.freeze([
            {
                class: new wealthify.Prop('label', {required: true}),
                callback: () => this.#updateLabel()
            },{
                class: new wealthify.Prop('link'),
                callback: () => this.#updateLink()
            },
            {
                class: new wealthify.Prop('link-target', {
                    default: this.constructor.defaultLinkTarget,
                    type: wealthify.propTypes.ArrayItem(this.constructor.validLinkTargets)
                }),
                callback: () => this.#updateLink()
            },
            {
                class: new wealthify.Prop('size', {
                    default: this.constructor.defaultSize,
                    type: wealthify.propTypes.ArrayItem(this.constructor.validSizes)
                }),
                callback: () => this.#updateSize()
            },{
                class: new wealthify.Prop('submit-form'),
                callback: () => this.#setSubmitCallback()
            },{
                class: new wealthify.Prop('variant', {
                    default: this.constructor.defaultVariant,
                    type: wealthify.propTypes.ArrayItem(this.constructor.validVariants)
                }),
                callback: () => this.#updateVariant()
            },{
                class: wealthify.props.Disabled({name: 'disabled-prop'}),
                callback: () => this.#updateDisabled()
            },{class: wealthify.props.Icon({
                default: 'no-icon',
                name: 'iconleft'
            })},{class: wealthify.props.Icon({
                default: 'no-icon',
                name: 'iconright'
            })},
            {
                class: new wealthify.Prop('no-label', {
                    type: wealthify.propTypes.Boolean(),
                    default: false
                }),
                callback: () => this.#updateNoLabel()
            }
        ]);

        // Private

        get #form () {
            // Will use implicit form by default
            let formElement = this.dom.button.form ? this.dom.button.form : null;

            if (this.#isViaFormReference) {
                // Use form that was passed explicitly (form ID passed)
                formElement = document.querySelector(`#${this.submitForm}`);
            }

            return formElement;
        }

        get #isViaImplicitForm () {
            // Button should use native form submit (positioned in form)
            return this.dom.button.form !== null && this.submitForm == 'true';
        }

        get #isViaFormReference () {
            // Button should use native form submit (not positioned in form)
            return this.submitForm && !this.#isViaFunctionReference && !this.#isViaImplicitForm;
        }

        get #isViaFunctionReference () {
            // Button should use function reference via native form submit (position in form)
            return this.dom.button.form !== null && this.submitForm && wealthify.type.function.isFunction(this.submitForm);
        }

        #updateLabel () {
            if(!this.label){
                return;
            }

            this.dom.content.innerHTML = this.label;
        }

        // assigned method and passed as a reference so handler can be removed if required.
        #updatePageByReference = (() =>
            window.open(this.link, `_${this.linkTarget}`)
        ).bind(this);

        #updateLink () {
            if(!this.link){
                this.dom.button.removeEventListener('click', this.#updatePageByReference);

                return;
            }

            this.dom.button.addEventListener('click', this.#updatePageByReference);
        }

        #submitFormByReference = ((event) => {
            event.preventDefault();

            // Use timeless setTimeout to push this event to the back of the call stack
            // This is to ensure any click handlers added to the button
            // Can be fired before form submission
            setTimeout(
                () => {
                    const formValid = wealthify.form.validateFieldsAndRefocus(this.#form);
                    // if a recaptcha field exists, we currently need to wait for it to be
                    // populated by a value when the user passes the check before the form
                    // can be submitted
                    const recaptchaField = this.#form.querySelector('[data-js="recaptchaResponse"]');
                    const recaptchaSuccess = recaptchaField && recaptchaField.value !== '';

                    if (!formValid) {
                        return;
                    }

                    if (!recaptchaField || recaptchaSuccess) {
                        this.disabledProp = true;

                        if (this.#isViaImplicitForm || this.#isViaFormReference) {
                            this.#form.submit();
                        } else if (this.#isViaFunctionReference) {
                            event.preventDefault();

                            if (wealthify.form.isDirty(this.#form)) {
                                this.submitForm();
                            }
                        }
                    }
                }
            );
        }).bind(this);

        #setSubmitCallback () {
            if (!this.#form) {
                return wealthify.error.throwError('Button base', 'The button needs to be associated with a form.');
            }

            this.dom.button.type = 'submit';

            if (this.#isViaFormReference) {

                this.addEventListener('click', this.#submitFormByReference);

            } else if (this.#isViaFunctionReference) {

                // Add fake submit button to allow native form submit behaviour, e.g. press Enter in form
                const nativeSubmitButton = document.createElement('button');

                nativeSubmitButton.type = 'submit';
                nativeSubmitButton.classList.add('u-display__none');
                this.after(nativeSubmitButton);

                this.dom.button.type = 'button';
                this.addEventListener('click', this.#submitFormByReference);
            }

            this.#form.addEventListener('submit', this.#submitFormByReference, true);
        }

        #updateSize () {
            this.constructor.validSizes.forEach((size) => this.dom.root.classList.remove(`${this.styleSelector}--${size}`));
            this.dom.root.classList.add(`${this.styleSelector}--${this.size}`);

            this.#updateIconSize();
        }

        #updateIconSize () {
            const iconSize = this.noLabel
                ? this.constructor.iconNoLabelSize
                : this.constructor.iconSizeMap[this.size];

            this.dom.iconright.size = iconSize;
            this.dom.iconleft.size = iconSize;
        }

        #updateVariant () {
            this.constructor.validVariants.forEach((variant) => this.classList.remove(`${this.styleSelector}--${variant}`));
            this.classList.add(`${this.styleSelector}--${this.variant}`);
        }

        #updateDisabled () {
            const classListAction = this.disabledProp ? 'add' : 'remove';

            if(this.disabledProp){
                this.dom.button.setAttribute('disabled', true);
            }else{
                this.dom.button.removeAttribute('disabled');
            }

            this.classList[classListAction](`${this.styleSelector}--disabled`);
        }

        #updateNoLabel () {
            const classListAction = this.noLabel ? 'add' : 'remove';

            this.dom.root.classList[classListAction](`${this.styleSelector}--no-label`);

            if(this.noLabel){
                this.dom.content.textContent = '';
                this.dom.content.setAttribute('aria-label', this.label);
            } else {
                this.dom.content.removeAttribute('aria-label');
                this.#updateLabel();
            }

            this.#updateIconSize();
        }
    }
);